import type { ReactiveString, RequestOptions } from "../types/request";
import type { ServiceName } from "../types/service"

/**
 * @typedef {Object} addBillingCycleFormData Billing Cycle's data
 * @prop {string} name - Name
 * @prop {string} type - Type
 */
export type addBillingCycleFormData = {
	name: string;
	type: string;
};

/**
 * @typedef {Object} editBillingCycleFormData Billing Cycle's data
 * @prop {string=} name - Name
 * @prop {string=} type - Type
 */
export type editBillingCycleFormData = {
	name?: string;
	type?: string;
};

/**
 * @typedef {Object} addCartServiceItemFormData Cart Service Item's data
 * @prop {string} cart_id - Cart Id
 * @prop {string} service_id - Service Id
 * @prop {Number} price - Price
 * @prop {Number} occurence - Occurence - Number of month
 * @prop {string} start_date - Start Date
 */
export type addCartServiceItemFormData = {
	cart_id: string;
	service_id: string;
	price: Number;
	occurence: Number;
	start_date: string;
};

/**
 * @typedef {Object} editCartServiceItemFormData Cart Service Item's data
 * @prop {Number=} price - Price
 * @prop {Number=} occurence - Occurence - Number of month 
 * @prop {string=} start_date - Start Date
 */
export type editCartServiceItemFormData = {
	price?: Number;
	occurence?: Number;
	start_date?: string;
};

/**
 * @typedef {Object} addCartTextileItemFormData
 * @prop {string} cart_id - Cart Id
 * @prop {string} platform_id - Platform Id
 * @prop {string} organization_id - Organization Id
 * @prop {string} warehouse_id - Warehouse Id
 * @prop {string} textile_id - Textile Id
 * @prop {string} textile_item_id - Textile Item Id
 * @prop {string} organization_textile_code - Organization Textile Code
 * @prop {string|null=} organization_textile_name - Organization Textile Name
 * @prop {string} organization_textile_item_code - Organization Textile Item Code
 * @prop {string|null=} image_id - Image Id
 * @prop {Number} meter_quantity - Meter Quantity
 * @prop {boolean} sample - Sample
 * @prop {Number} unit_price - Unit Price
 * @prop {string} unit_type - Unit Type
 */
export type addCartTextileItemFormData = {
	cart_id: string;
	platform_id: string;
	organization_id: string;
	warehouse_id: string;
	textile_id: string;
	textile_item_id: string;
	organization_textile_code: string;
	organization_textile_name?: string|null;
	organization_textile_item_code: string;
	image_id?: string|null;
	meter_quantity: Number;
	sample: boolean;
	unit_price: Number;
	unit_type: string;
};

/**
 * @typedef {Object} editCartTextileItemFormData Cart Textile Item's data
 * @prop {string=} cart_id - Cart Id
 * @prop {string=} platform_id - Platform Id
 * @prop {string=} organization_id - Organization Id
 * @prop {string=} warehouse_id - Warehouse Id
 * @prop {string=} textile_id - Textile Id
 * @prop {string=} textile_item_id - Textile Item Id
 * @prop {string=} organization_textile_code - Organization Textile Code
 * @prop {string|null=} organization_textile_name - Organization Textile Name
 * @prop {string=} organization_textile_item_code - Organization Textile Item Code
 * @prop {string|null=} image_id - Image Id
 * @prop {Number=} meter_quantity - Meter Quantity
 * @prop {boolean=} sample - Sample
 * @prop {Number=} unit_price - Unit Price
 * @prop {string=} unit_type - Unit Type
 */
export type editCartTextileItemFormData = {
	cart_id?: string;
	platform_id?: string;
	organization_id?: string;
	warehouse_id?: string;
	textile_id?: string;
	textile_item_id?: string;
	organization_textile_code?: string;
	organization_textile_name?: string|null;
	organization_textile_item_code?: string;
	image_id?: string|null;
	meter_quantity?: Number;
	sample?: boolean;
	unit_price?: Number;
	unit_type?: string;
};

/**
 * @typedef {Object} editQuantityCartTextileItemFormData Quantity
 * @prop {Number} meter_quantity - Meter Quantity
 */
export type editQuantityCartTextileItemFormData = {
	meter_quantity: Number;
};

/**
 * @typedef {Object} addCartFormData Cart's data
 * @prop {string} platform_id
 * @prop {string|null} ecosystem_transaction_fee_id - Ecosystem Transaction Fee Id
 * @prop {string|null=} client_organization_id - Client Organization Id
 * @prop {string} organization_id - Organization Id
 * @prop {string|null=} user_id - User Id
 * @prop {string|null=} guest_id - Guest Id
 * @prop {Number} status - Status: 0 - Not active | 1 - Active| 2 - Idle | 3 - In Checkout | 4 - Processed (0, 1, 2, 3, 4)
 * @prop {string|null=} product_order_id - Product Order Id
 */
export type addCartFormData = {
	platform_id: string;
	ecosystem_transaction_fee_id: string|null;
	client_organization_id?: string|null;
	organization_id: string;
	user_id?: string|null;
	guest_id?: string|null;
	status: Number;
	product_order_id?: string|null;
};

/**
 * @typedef {Object} editCartFormData Cart's data
 * @prop {string|null=} ecosystem_transaction_fee_id - Ecosystem Transaction Fee Id
 * @prop {string|null=} client_organization_id - Client Organization Id
 * @prop {string=} organization_id - Organization Id
 * @prop {string|null=} user_id - User Id
 * @prop {string|null=} guest_id - Guest Id
 * @prop {Number=} status - Status: 0 - Not active | 1 - Active| 2 - Idle | 3 - In Checkout | 4 - Processed (0, 1, 2, 3, 4)
 * @prop {string|null=} product_order_id - Product Order Id
 */
export type editCartFormData = {
	ecosystem_transaction_fee_id?: string|null;
	client_organization_id?: string|null;
	organization_id?: string;
	user_id?: string|null;
	guest_id?: string|null;
	status?: Number;
	product_order_id?: string|null;
};

/**
 * @typedef {Object} isTextileItemInCartCartFormData
 * @prop {string} textile_item_id
 * @prop {string} platform_id
 * @prop {boolean=} sample
 */
export type isTextileItemInCartCartFormData = {
	textile_item_id: string;
	platform_id: string;
	sample?: boolean;
};

/**
 * @typedef {Object} getPricesCartFormData
 * @prop {string|null=} continent - Continent where the buyer wants it delivered
 * @prop {string|null=} ship_to_address - Address where the order is delivered
 * @prop {string|null=} country - Country where the order is delivered
 * @prop {string|null=} state - State where the order is delivered
 */
export type getPricesCartFormData = {
	continent?: string|null;
	ship_to_address?: string|null;
	country?: string|null;
	state?: string|null;
};

/**
 * @typedef {Object} addEcosystemTransactionFeeFormData Ecosystem Transaction Fee's data
 * @prop {string} fee_type - Fee Type - value or percentage
 * @prop {Number} fee_amount - Fee Amount
 * @prop {boolean} item_specific - Item Specific
 * @prop {string} item - Item
 */
export type addEcosystemTransactionFeeFormData = {
	fee_type: string;
	fee_amount: Number;
	item_specific: boolean;
	item: string;
};

/**
 * @typedef {Object} editEcosystemTransactionFeeFormData Ecosystem Transaction Fee's data
 * @prop {string=} fee_type - Fee Type - value or percentage
 * @prop {Number=} fee_amount - Fee Amount
 * @prop {boolean=} item_specific - Item Specific
 * @prop {string=} item - Item
 */
export type editEcosystemTransactionFeeFormData = {
	fee_type?: string;
	fee_amount?: Number;
	item_specific?: boolean;
	item?: string;
};

/**
 * @typedef {Object} addLocationPricingRuleFormData Location Pricing Rule's data
 * @prop {string} pricing_strategy_id - Pricing Strategies Id
 * @prop {Number} zone_number - Zone Number
 * @prop {string} continent - Continent - North America | South America | Central America | Europe | Asia | Oceania | Africa
 * @prop {Number} percentage - Percentage
 */
export type addLocationPricingRuleFormData = {
	pricing_strategy_id: string;
	zone_number: Number;
	continent: string;
	percentage: Number;
};

/**
 * @typedef {Object} editLocationPricingRuleFormData Location Pricing Rule's data
 * @prop {string=} pricing_strategy_id - Pricing Strategies Id
 * @prop {Number=} zone_number - Zone Number
 * @prop {string=} continent - Continent - North America | South America | Central America | Europe | Asia | Oceania | Africa
 * @prop {Number=} percentage - Percentage
 */
export type editLocationPricingRuleFormData = {
	pricing_strategy_id?: string;
	zone_number?: Number;
	continent?: string;
	percentage?: Number;
};

/**
 * @typedef {Object} listLoyaltyProgramMembersFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Order rule to apply to the paging, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listLoyaltyProgramMembersFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addLoyaltyProgramMemberFormData Loyalty Program Member's data
 * @prop {string} loyalty_program_id - Loyalty Program Id
 * @prop {string} loyalty_program_tier_id - Loyalty Program Tier Id
 * @prop {string|null=} user_id - User Id. Should have user_id or organization_id not null, not both null or not both define
 * @prop {string|null=} organization_id - Organization Id. Should have user_id or organization_id not null, not both null or not both define
 */
export type addLoyaltyProgramMemberFormData = {
	loyalty_program_id: string;
	loyalty_program_tier_id: string;
	user_id?: string|null;
	organization_id?: string|null;
};

/**
 * @typedef {Object} editLoyaltyProgramMemberFormData Loyalty Program Member's data
 * @prop {string=} loyalty_program_id - Loyalty Program Id
 * @prop {string=} loyalty_program_tier_id - Loyalty Program Tier Id
 * @prop {string|null=} user_id - User Id. Should have user_id or organization_id not null, not both null or not both define
 * @prop {string|null=} organization_id - Organization Id. Should have user_id or organization_id not null, not both null or not both define
 */
export type editLoyaltyProgramMemberFormData = {
	loyalty_program_id?: string;
	loyalty_program_tier_id?: string;
	user_id?: string|null;
	organization_id?: string|null;
};

/**
 * @typedef {Object} listLoyaltyProgramTiersFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Order rule to apply to the paging, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listLoyaltyProgramTiersFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addLoyaltyProgramTierFormData Loyalty Program Tier's data
 * @prop {string} loyalty_program_id - Loyalty Program Id
 * @prop {Number} sequence - Sequence
 * @prop {string} tier_name - Tier Name
 * @prop {string} tier_min_amount - Tier Min Amount
 * @prop {string|null=} tier_max_amount - Tier Max Amount
 * @prop {Number} percentage - Percentage
 */
export type addLoyaltyProgramTierFormData = {
	loyalty_program_id: string;
	sequence: Number;
	tier_name: string;
	tier_min_amount: string;
	tier_max_amount?: string|null;
	percentage: Number;
};

/**
 * @typedef {Object} editLoyaltyProgramTierFormData Loyalty Program Tier's data
 * @prop {string=} loyalty_program_id - Loyalty Program Id
 * @prop {Number=} sequence - Sequence
 * @prop {string=} tier_name - Tier Name
 * @prop {string=} tier_min_amount - Tier Min Amount
 * @prop {string|null=} tier_max_amount - Tier Max Amount
 * @prop {Number=} percentage - Percentage
 */
export type editLoyaltyProgramTierFormData = {
	loyalty_program_id?: string;
	sequence?: Number;
	tier_name?: string;
	tier_min_amount?: string;
	tier_max_amount?: string|null;
	percentage?: Number;
};

/**
 * @typedef {Object} addLoyaltyProgramFormData Loyalty Program's data
 * @prop {string} pricing_strategy_id - Pricing Strategy Id
 * @prop {string} platform_id - Platform Id
 */
export type addLoyaltyProgramFormData = {
	pricing_strategy_id: string;
	platform_id: string;
};

/**
 * @typedef {Object} editLoyaltyProgramFormData Loyalty Program's data
 * @prop {string=} pricing_strategy_id - Pricing Strategy Id
 * @prop {string=} platform_id - Platform Id
 */
export type editLoyaltyProgramFormData = {
	pricing_strategy_id?: string;
	platform_id?: string;
};

/**
 * @typedef {Object} customizedViewForOrganizationIdLoyaltyProgramFormData Optional request's data. Provide user_id or organization_id
 * @prop {string|null=} user_id - Client User ID
 * @prop {string|null=} organization_id - Client Organization ID
 */
export type customizedViewForOrganizationIdLoyaltyProgramFormData = {
	user_id?: string|null;
	organization_id?: string|null;
};

/**
 * @typedef {Object} listOriginWarehousesFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Order rule to apply to the paging, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listOriginWarehousesFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addOriginWarehouseFormData Origin Warehouse's data
 * @prop {string} sales_taxe_id - Sales Taxe Id
 * @prop {string} warehouse_id - Warehouse Id
 * @prop {Number} status - Status (0, 1, 99)
 */
export type addOriginWarehouseFormData = {
	sales_taxe_id: string;
	warehouse_id: string;
	status: Number;
};

/**
 * @typedef {Object} editOriginWarehouseFormData Origin Warehouse's data
 * @prop {string=} sales_taxe_id - Sales Taxe Id
 * @prop {string=} warehouse_id - Warehouse Id
 * @prop {Number=} status - Status (0, 1, 99)
 */
export type editOriginWarehouseFormData = {
	sales_taxe_id?: string;
	warehouse_id?: string;
	status?: Number;
};

/**
 * @typedef {Object} listPlatformYearlyTotalsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Order rule to apply to the paging, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listPlatformYearlyTotalsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} getYearlySpendingByClientPlatformYearlyTotalFormData
 * @prop {string|null=} user_id
 * @prop {string|null=} FormData - organization_id
 */
export type getYearlySpendingByClientPlatformYearlyTotalFormData = {
	user_id?: string|null;
	FormData?: string|null;
};

/**
 * @typedef {Object} incrementYearlyTotalPlatformYearlyTotalFormData Total spent this year data
 * @prop {string} user_id - User Id
 * @prop {string|null=} organization_id - Organization Id
 * @prop {string} platform_id - Platform Id
 * @prop {Number} amount - Amount spent on a platform
 */
export type incrementYearlyTotalPlatformYearlyTotalFormData = {
	user_id: string;
	organization_id?: string|null;
	platform_id: string;
	amount: Number;
};

/**
 * @typedef {Object} addPlatformYearlyTotalFormData Platform Yearly Total's data
 * @prop {string|null=} user_id - User Id
 * @prop {string|null=} organization_id - Organization Id
 * @prop {string} platform_id - Platform Id
 * @prop {Number} yearly_total_amount - Yearly Total Amount
 * @prop {string} yearly_total_unit - Yearly Total Unit
 */
export type addPlatformYearlyTotalFormData = {
	user_id?: string|null;
	organization_id?: string|null;
	platform_id: string;
	yearly_total_amount: Number;
	yearly_total_unit: string;
};

/**
 * @typedef {Object} editPlatformYearlyTotalFormData Platform Yearly Total's data
 * @prop {string|null=} user_id - User Id
 * @prop {string|null=} organization_id - Organization Id
 * @prop {string=} platform_id - Platform Id
 * @prop {Number=} yearly_total_amount - Yearly Total Amount
 * @prop {string=} yearly_total_unit - Yearly Total Unit
 */
export type editPlatformYearlyTotalFormData = {
	user_id?: string|null;
	organization_id?: string|null;
	platform_id?: string;
	yearly_total_amount?: Number;
	yearly_total_unit?: string;
};

/**
 * @typedef {Object} addPlatformsTransactionFeeFormData Platforms Transaction Fee's data
 * @prop {string} platform_id - Platform Id
 * @prop {string} shipping_fee_type - Shipping Fee Type
 * @prop {Number} shipping_fee_amount - Shipping Fee Amount
 * @prop {string} transaction_fee_type - Transaction Fee Type
 * @prop {Number} transaction_fee_amount - Transaction Fee Amount
 */
export type addPlatformsTransactionFeeFormData = {
	platform_id: string;
	shipping_fee_type: string;
	shipping_fee_amount: Number;
	transaction_fee_type: string;
	transaction_fee_amount: Number;
};

/**
 * @typedef {Object} editPlatformsTransactionFeeFormData Platforms Transaction Fee's data
 * @prop {string=} platform_id - Platform Id
 * @prop {string=} shipping_fee_type - Shipping Fee Type
 * @prop {Number=} shipping_fee_amount - Shipping Fee Amount
 * @prop {string=} transaction_fee_type - Transaction Fee Type
 * @prop {Number=} transaction_fee_amount - Transaction Fee Amount
 */
export type editPlatformsTransactionFeeFormData = {
	platform_id?: string;
	shipping_fee_type?: string;
	shipping_fee_amount?: Number;
	transaction_fee_type?: string;
	transaction_fee_amount?: Number;
};

/**
 * @typedef {Object} addPriceFormData Price's data
 * @prop {string} pricing_model_id - Pricing Models Id
 * @prop {Number} amount - Amount
 */
export type addPriceFormData = {
	pricing_model_id: string;
	amount: Number;
};

/**
 * @typedef {Object} editPriceFormData Price's data
 * @prop {string=} pricing_model_id - Pricing Models Id
 * @prop {Number=} amount - Amount
 */
export type editPriceFormData = {
	pricing_model_id?: string;
	amount?: Number;
};

/**
 * @typedef {Object} getTextileItemsPricesPriceFormData
 * @prop {{}} textile_items - Textile Item Ids
 * @prop {string|null=} continent - Continent
 * @prop {string|null=} platform_id - Platform ID
 */
export type getTextileItemsPricesPriceFormData = {
	textile_items: {};
	continent?: string|null;
	platform_id?: string|null;
};

/**
 * @typedef {Object} listPricingAgreementsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Order rule to apply to the paging, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listPricingAgreementsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addPricingAgreementFormData Pricing Agreement's data
 * @prop {string=} organization_id - Organization id of the merchant creating the agreement.
 * @prop {{}} client_organization_ids - Textile id under which the agreement and the textile items are under.
 * @prop {string=} textile_id - Organization id of the merchant creating the agreement.
 * @prop {{}} textile_item_ids - Client with wich the pricing agreement is with
 * @prop {string=} specific_price_unit_type - Organization id of the merchant creating the agreement.
 * @prop {Number=} specific_price_unit_amount - Organization id of the merchant creating the agreement.
 * @prop {string} minimum_volume_unit_type - Minimum volume unit type for minimum volume per year
 * @prop {Number} minimum_volume_unit_amount - Minimum volume per year amount
 * @prop {string} agreement_end_date - Agreement End Date
 * @prop {Number} status - Status
 * @prop {string=} shipping_terms - Shipping Terms
 * @prop {string=} payment_terms - Payment Terms
 * @prop {string=} client_reviewer_id - Client Reviewer Id
 * @prop {string=} client_review_date - Client Review Date
 */
export type addPricingAgreementFormData = {
	organization_id?: string;
	client_organization_ids: {};
	textile_id?: string;
	textile_item_ids: {};
	specific_price_unit_type?: string;
	specific_price_unit_amount?: Number;
	minimum_volume_unit_type: string;
	minimum_volume_unit_amount: Number;
	agreement_end_date: string;
	status: Number;
	shipping_terms?: string;
	payment_terms?: string;
	client_reviewer_id?: string;
	client_review_date?: string;
};

/**
 * @typedef {Object} viewForOrganizationByClientsForTextileIdPricingAgreementFormData Pricing Agreement's data
 * @prop {string=} textile_id - Organization id of the merchant creating the agreement.
 */
export type viewForOrganizationByClientsForTextileIdPricingAgreementFormData = {
	textile_id?: string;
};

/**
 * @typedef {Object} viewForOrganizationByClientsForTextileItemIdPricingAgreementFormData Pricing Agreement's textile and textile item id
 * @prop {string=} textile_id - Organization id of the merchant creating the agreement.
 * @prop {string=} textile_item_id - Organization id of the merchant creating the agreement.
 */
export type viewForOrganizationByClientsForTextileItemIdPricingAgreementFormData = {
	textile_id?: string;
	textile_item_id?: string;
};

/**
 * @typedef {Object} viewForOrganizationbyClientIdPricingAgreementFormData
 * @prop {string} client_organization_id - Organization id of the client for the agreement.
 */
export type viewForOrganizationbyClientIdPricingAgreementFormData = {
	client_organization_id: string;
};

/**
 * @typedef {Object} editPricingAgreementFormData Pricing Agreement's data
 * @prop {string} client_organization_id - Textile id under which the agreement and the textile items are under.
 * @prop {string=} textile_id - Organization id of the merchant creating the agreement.
 * @prop {string} textile_item_id - Client with wich the pricing agreement is with
 * @prop {string=} specific_price_unit_type - Organization id of the merchant creating the agreement.
 * @prop {Number=} specific_price_unit_amount - Organization id of the merchant creating the agreement.
 * @prop {string} minimum_volume_unit_type - Minimum volume unit type for minimum volume per year
 * @prop {Number} minimum_volume_unit_amount - Minimum volume per year amount
 * @prop {string} agreement_end_date - Agreement End Date
 * @prop {Number} status - Status
 * @prop {string=} shipping_terms - Shipping Terms
 * @prop {string=} payment_terms - Payment Terms
 * @prop {string=} client_reviewer_id - Client Reviewer Id
 * @prop {string=} client_review_date - Client Review Date
 */
export type editPricingAgreementFormData = {
	client_organization_id: string;
	textile_id?: string;
	textile_item_id: string;
	specific_price_unit_type?: string;
	specific_price_unit_amount?: Number;
	minimum_volume_unit_type: string;
	minimum_volume_unit_amount: Number;
	agreement_end_date: string;
	status: Number;
	shipping_terms?: string;
	payment_terms?: string;
	client_reviewer_id?: string;
	client_review_date?: string;
};

/**
 * @typedef {Object} editAgreementsForTextilePricingAgreementFormData Pricing Agreement's data
 * @prop {string=} organization_id - Organization id of the merchant creating the agreement.
 * @prop {{}} client_organization_ids - Textile id under which the agreement and the textile items are under.
 * @prop {string=} textile_id - Organization id of the merchant creating the agreement.
 * @prop {{}} textile_item_ids - Client with wich the pricing agreement is with
 * @prop {string=} specific_price_unit_type - Organization id of the merchant creating the agreement.
 * @prop {Number=} specific_price_unit_amount - Organization id of the merchant creating the agreement.
 * @prop {string} minimum_volume_unit_type - Minimum volume unit type for minimum volume per year
 * @prop {Number} minimum_volume_unit_amount - Minimum volume per year amount
 * @prop {string} agreement_end_date - Agreement End Date
 * @prop {Number} status - Status
 * @prop {string=} shipping_terms - Shipping Terms
 * @prop {string=} payment_terms - Payment Terms
 * @prop {string=} client_reviewer_id - Client Reviewer Id
 * @prop {string=} client_review_date - Client Review Date
 */
export type editAgreementsForTextilePricingAgreementFormData = {
	organization_id?: string;
	client_organization_ids: {};
	textile_id?: string;
	textile_item_ids: {};
	specific_price_unit_type?: string;
	specific_price_unit_amount?: Number;
	minimum_volume_unit_type: string;
	minimum_volume_unit_amount: Number;
	agreement_end_date: string;
	status: Number;
	shipping_terms?: string;
	payment_terms?: string;
	client_reviewer_id?: string;
	client_review_date?: string;
};

/**
 * @typedef {Object} addPricingModelTierFormData Pricing Model Tier's data
 * @prop {string} pricing_tier_group_id - Pricing Tier Groups Id
 * @prop {string=} price_id - Price Id
 * @prop {string} name - Name
 * @prop {string} quota_unit_type - Quota Unit Type
 * @prop {Number} yearly_quota - Yearly Quota
 * @prop {Number} monthly_quota - Monthly Quota
 * @prop {Number} unit_price - Unit Price
 * @prop {Number} total_price - Total Price
 * @prop {string} period_unit_type - Period Unit Type
 * @prop {Number} period_unit - Period Unit
 * @prop {boolean} status - Status
 */
export type addPricingModelTierFormData = {
	pricing_tier_group_id: string;
	price_id?: string;
	name: string;
	quota_unit_type: string;
	yearly_quota: Number;
	monthly_quota: Number;
	unit_price: Number;
	total_price: Number;
	period_unit_type: string;
	period_unit: Number;
	status: boolean;
};

/**
 * @typedef {Object} editPricingModelTierFormData Pricing Model Tier's data
 * @prop {string=} pricing_tier_group_id - Pricing Tier Groups Id
 * @prop {string=} price_id - Price Id
 * @prop {string=} name - Name
 * @prop {string=} quota_unit_type - Quota Unit Type
 * @prop {Number=} yearly_quota - Yearly Quota
 * @prop {Number=} monthly_quota - Monthly Quota
 * @prop {Number=} unit_price - Unit Price
 * @prop {Number=} total_price - Total Price
 * @prop {string=} period_unit_type - Period Unit Type
 * @prop {Number=} period_unit - Period Unit
 * @prop {boolean=} status - Status
 */
export type editPricingModelTierFormData = {
	pricing_tier_group_id?: string;
	price_id?: string;
	name?: string;
	quota_unit_type?: string;
	yearly_quota?: Number;
	monthly_quota?: Number;
	unit_price?: Number;
	total_price?: Number;
	period_unit_type?: string;
	period_unit?: Number;
	status?: boolean;
};

/**
 * @typedef {Object} addPricingModelFormData Pricing Model's data
 * @prop {string=} revenue_model_id - Revenue Models Id
 * @prop {string} name - Name - Flat rate - Tier based - Unit based - Per member
 * @prop {string} type - Type - price | pricing_tier_groups
 * @prop {Number} status - Status
 */
export type addPricingModelFormData = {
	revenue_model_id?: string;
	name: string;
	type: string;
	status: Number;
};

/**
 * @typedef {Object} editPricingModelFormData Pricing Model's data
 * @prop {string} revenue_model_id - Revenue Model Id
 * @prop {string=} name - Name - Flat rate - Tier based - Unit based - Per member
 * @prop {string=} type - Type - price | pricing_tier_groups
 * @prop {Number=} status - Status
 */
export type editPricingModelFormData = {
	revenue_model_id: string;
	name?: string;
	type?: string;
	status?: Number;
};

/**
 * @typedef {Object} addPricingStrategyFormData Pricing Strategy's data
 * @prop {string} organization_id - Organization ID
 */
export type addPricingStrategyFormData = {
	organization_id: string;
};

/**
 * @typedef {Object} editPricingStrategyFormData Pricing Strategy's data
 * @prop {string=} organization_id - Organization ID
 */
export type editPricingStrategyFormData = {
	organization_id?: string;
};

/**
 * @typedef {Object} addEditLoyaltyProgramTierPricingStrategyFormData Add or Edit a LoyaltyProgramTier record
 * @prop {string} organization_id - Organization ID
 * @prop {string} platform_id - Platform ID
 * @prop {string=} loyalty_program_id - LoyaltyProgram ID
 * @prop {string} tier_name - Tier Name
 * @prop {Number} sequence - Tier Sequence
 * @prop {string} tier_min_amount - Tier Minimum Volume
 * @prop {string} tier_max_amount - Tier Maximum Volume
 * @prop {Number} percentage - Tier Percentage
 */
export type addEditLoyaltyProgramTierPricingStrategyFormData = {
	organization_id: string;
	platform_id: string;
	loyalty_program_id?: string;
	tier_name: string;
	sequence: Number;
	tier_min_amount: string;
	tier_max_amount: string;
	percentage: Number;
};

/**
 * @typedef {Object} addPricingTierGroupFormData Pricing Tier Group's data
 * @prop {string} pricing_model_id - Pricing Models Id
 * @prop {string} name - Name
 * @prop {string} description - Description
 * @prop {{}=} pricing_model_tiers - Pricing Model Tier's data
 */
export type addPricingTierGroupFormData = {
	pricing_model_id: string;
	name: string;
	description: string;
	pricing_model_tiers?: {};
};

/**
 * @typedef {Object} editPricingTierGroupFormData Pricing Tier Group's data
 * @prop {string=} pricing_model_id - Pricing Models Id
 * @prop {string=} name - Name
 * @prop {string=} description - Description
 */
export type editPricingTierGroupFormData = {
	pricing_model_id?: string;
	name?: string;
	description?: string;
};

/**
 * @typedef {Object} addRevenueModelFormData Revenue Model's data
 * @prop {string} name - Name
 * @prop {Number} status - Status
 * @prop {string} billing_cycle_id - Billing Cycles Id
 */
export type addRevenueModelFormData = {
	name: string;
	status: Number;
	billing_cycle_id: string;
};

/**
 * @typedef {Object} editRevenueModelFormData Revenue Model's data
 * @prop {string=} name - Name
 * @prop {Number=} status - Status
 * @prop {string=} billing_cycle_id - Billing Cycles Id
 */
export type editRevenueModelFormData = {
	name?: string;
	status?: Number;
	billing_cycle_id?: string;
};

/**
 * @typedef {Object} listSalesTaxesFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Order rule to apply to the paging, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listSalesTaxesFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} viewForOrganizationSalesTaxFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Order rule to apply to the paging, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type viewForOrganizationSalesTaxFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} getApplicableTaxSalesTaxFormData
 * @prop {{}=} contexts - Tax context information
 */
export type getApplicableTaxSalesTaxFormData = {
	contexts?: {};
};

/**
 * @typedef {Object} addSalesTaxFormData Sales Tax's data
 * @prop {string} pricing_strategy_id - Pricing Strategies Id
 * @prop {Number} tax_percentage - Tax Percentage
 * @prop {{}} warehouses - Warehouse IDs
 * @prop {{}} countries - Countries
 */
export type addSalesTaxFormData = {
	pricing_strategy_id: string;
	tax_percentage: Number;
	warehouses: {};
	countries: {};
};

/**
 * @typedef {Object} editSalesTaxFormData Sales Tax's data
 * @prop {string=} pricing_strategy_id - Pricing Strategy Id
 * @prop {Number|null=} tax_percentage - Tax Percentage
 * @prop {string|null=} country - Country
 * @prop {string|null=} state - State
 * @prop {string|null=} tax_name - Tax Name
 */
export type editSalesTaxFormData = {
	pricing_strategy_id?: string;
	tax_percentage?: Number|null;
	country?: string|null;
	state?: string|null;
	tax_name?: string|null;
};

/**
 * @typedef {Object} listServiceFeesFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Order rule to apply to the paging, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listServiceFeesFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addServiceFeeFormData Service Fee's data
 * @prop {string} pricing_strategy_id - Pricing Strategy Id
 * @prop {string} platform_id - Platform Id
 * @prop {string|null=} type - Type
 * @prop {Number|null=} percentage - Percentage
 */
export type addServiceFeeFormData = {
	pricing_strategy_id: string;
	platform_id: string;
	type?: string|null;
	percentage?: Number|null;
};

/**
 * @typedef {Object} editServiceFeeFormData Service Fee's data
 * @prop {string=} pricing_strategy_id - Pricing Strategy Id
 * @prop {string=} platform_id - Platform Id
 * @prop {string|null=} type - Type
 * @prop {Number|null=} percentage - Percentage
 */
export type editServiceFeeFormData = {
	pricing_strategy_id?: string;
	platform_id?: string;
	type?: string|null;
	percentage?: Number|null;
};

/**
 * @typedef {Object} addOrganizationDefaultsForMarketplaceSubscriptionFormData Pricing Strategy's data
 * @prop {string} organization_id - Organization ID of the organization subscribing
 * @prop {string} service_id - Service ID of the service the organization is subscribing to
 * @prop {string} platform_id - Platform ID of the platform to be managed by the organization subscribing
 */
export type addOrganizationDefaultsForMarketplaceSubscriptionFormData = {
	organization_id: string;
	service_id: string;
	platform_id: string;
};

/**
 * @typedef {Object} addOrganizationDefaultsForPartnerSubscriptionFormData Pricing Strategy's data
 * @prop {string} organization_id - Organization ID of the organization subscribing
 * @prop {string} service_id - Service ID of the service the organization is subscribing to
 */
export type addOrganizationDefaultsForPartnerSubscriptionFormData = {
	organization_id: string;
	service_id: string;
};

/**
 * @typedef {Object} addTextileItemsPricingFormData Textile Items Pricing's data
 * @prop {string} textiles_pricing_id - Textiles Pricing Id
 * @prop {string} textile_item_id - Textile Item Id
 * @prop {Number} cost - Cost
 * @prop {Number} minimum_price_sold - Minimum Price Sold
 */
export type addTextileItemsPricingFormData = {
	textiles_pricing_id: string;
	textile_item_id: string;
	cost: Number;
	minimum_price_sold: Number;
};

/**
 * @typedef {Object} editTextileItemsPricingFormData Textile Items Pricing's data
 * @prop {string=} textiles_pricing_id - Textiles Pricing Id
 * @prop {string=} textile_item_id - Textile Item Id
 * @prop {Number=} cost - Cost
 * @prop {Number=} minimum_price_sold - Minimum Price Sold
 */
export type editTextileItemsPricingFormData = {
	textiles_pricing_id?: string;
	textile_item_id?: string;
	cost?: Number;
	minimum_price_sold?: Number;
};

/**
 * @typedef {Object} addTextilesPricingFormData Textiles Pricing's data
 * @prop {string} organization_id
 * @prop {{}} textiles_pricings - Textile Pricing's data
 */
export type addTextilesPricingFormData = {
	organization_id: string;
	textiles_pricings: {};
};

/**
 * @typedef {Object} editTextilesPricingFormData Textiles Pricing's data
 * @prop {string=} textile_id - Textile Id
 * @prop {Number=} default_cost - Default Cost
 * @prop {Number=} default_minimum_price_sold - Default Minimum Price Sold
 * @prop {string=} pricing_strategy_id - Pricing Strategy Id
 */
export type editTextilesPricingFormData = {
	textile_id?: string;
	default_cost?: Number;
	default_minimum_price_sold?: Number;
	pricing_strategy_id?: string;
};

/**
 * @typedef {Object} listVolumePricingMembersFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Order rule to apply to the paging, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listVolumePricingMembersFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addVolumePricingMemberFormData Volume Pricing Member's data
 * @prop {string} volume_pricing_rule_id - Volume Pricing Rule Id
 * @prop {string|null=} user_id - User Id
 * @prop {string|null=} organization_id - Organization Id
 */
export type addVolumePricingMemberFormData = {
	volume_pricing_rule_id: string;
	user_id?: string|null;
	organization_id?: string|null;
};

/**
 * @typedef {Object} editVolumePricingMemberFormData Volume Pricing Member's data
 * @prop {string} volume_pricing_rule_id - Volume Pricing Rule Id
 * @prop {string|null=} user_id - User Id
 * @prop {string|null=} organization_id - Organization Id
 */
export type editVolumePricingMemberFormData = {
	volume_pricing_rule_id: string;
	user_id?: string|null;
	organization_id?: string|null;
};

/**
 * @typedef {Object} addVolumePricingRuleFormData Volume Pricing Rule's data
 * @prop {string} pricing_strategy_id - Pricing Strategies Id
 * @prop {Number} sequence - Sequence number
 * @prop {string} tier_name - Tier Name
 * @prop {string} tier_unit_type - Tier Unit Type
 * @prop {Number} tier_min_volume - Tier Min Volume
 * @prop {Number} tier_max_volume - Tier Max Volume
 * @prop {Number} percentage - Percentage
 */
export type addVolumePricingRuleFormData = {
	pricing_strategy_id: string;
	sequence: Number;
	tier_name: string;
	tier_unit_type: string;
	tier_min_volume: Number;
	tier_max_volume: Number;
	percentage: Number;
};

/**
 * @typedef {Object} editVolumePricingRuleFormData Volume Pricing Rule's data
 * @prop {Number=} sequence - Sequence number
 * @prop {string=} tier_name - Tier Name
 * @prop {string=} tier_unit_type - Tier Unit Type
 * @prop {Number=} tier_min_volume - Tier Min Volume
 * @prop {Number=} tier_max_volume - Tier Max Volume
 * @prop {Number=} percentage - Percentage
 */
export type editVolumePricingRuleFormData = {
	sequence?: Number;
	tier_name?: string;
	tier_unit_type?: string;
	tier_min_volume?: Number;
	tier_max_volume?: Number;
	percentage?: Number;
};

/**
 * @typedef {Object} customizedViewForOrganizationIdVolumePricingRuleFormData Optional request's data. Provide user_id or organization_id
 * @prop {string|null=} user_id - Client User ID
 * @prop {string|null=} organization_id - Client Organization ID
 */
export type customizedViewForOrganizationIdVolumePricingRuleFormData = {
	user_id?: string|null;
	organization_id?: string|null;
};

const service = {
	name: "SCM" as ServiceName,
	version: 1
};

export const useSCMApi = () => {
	/**
	 * Call an endpoint on the SCM
	 *
	 * Sets the service to the Request and forwards it to the TengivaApi.call() function
	 * @param {ReactiveString} endpoint - Endpoint to request
	 * @param {RequestOptions} options - Additonal options for the Request
	 * @returns {Promise<any>}
	 */
	const call = async <T = any>(endpoint: ReactiveString, options: RequestOptions) => {
		options = {
			...options,
			service
		};
		return await useTengivaApi<T>(endpoint, options);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} billingCycle - Billing Cycle id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewBillingCycle = async <T = any>(billingCycle: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/billing_cycles/view`,
			[
				billingCycle
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewForRevenueModel method
	 * 
	 * @param {ReactiveString} revenue_model_id - RevenueModels id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForRevenueModelBillingCycle = async <T = any>(revenue_model_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/billing_cycles/viewForRevenueModel`,
			[
				revenue_model_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addBillingCycleFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addBillingCycle = async <T = any>(formData: addBillingCycleFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/billing_cycles/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} billingCycle - Billing Cycle id.
	 * @param {editBillingCycleFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editBillingCycle = async <T = any>(billingCycle: ReactiveString, formData: editBillingCycleFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/billing_cycles/edit`,
			[
				billingCycle
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} billingCycle - Billing Cycle id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteBillingCycle = async <T = any>(billingCycle: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/billing_cycles/delete`,
			[
				billingCycle
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} cartServiceItem - Cart Service Item id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewCartServiceItem = async <T = any>(cartServiceItem: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/cart_service_items/view`,
			[
				cartServiceItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addCartServiceItemFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addCartServiceItem = async <T = any>(formData: addCartServiceItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/cart_service_items/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} cartServiceItem - Cart Service Item id.
	 * @param {editCartServiceItemFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editCartServiceItem = async <T = any>(cartServiceItem: ReactiveString, formData: editCartServiceItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/cart_service_items/edit`,
			[
				cartServiceItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} cartServiceItem - Cart Service Item id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteCartServiceItem = async <T = any>(cartServiceItem: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/cart_service_items/delete`,
			[
				cartServiceItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewForCart method
	 * 
	 * @param {ReactiveString} cartId - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForCartCartServiceItem = async <T = any>(cartId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/cart_service_items/viewForCart`,
			[
				cartId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} cartTextileItem - Cart Textile Item id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewCartTextileItem = async <T = any>(cartTextileItem: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/cart_textile_items/view`,
			[
				cartTextileItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewForCart method
	 * 
	 * @param {ReactiveString} cartId - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForCartCartTextileItem = async <T = any>(cartId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/cart_textile_items/viewForCart`,
			[
				cartId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addCartTextileItemFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addCartTextileItem = async <T = any>(formData: addCartTextileItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/cart_textile_items/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} cartTextileItem - Cart Textile Item id.
	 * @param {editCartTextileItemFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editCartTextileItem = async <T = any>(cartTextileItem: ReactiveString, formData?: editCartTextileItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/cart_textile_items/edit`,
			[
				cartTextileItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * editQuantity method
	 * 
	 * @param {ReactiveString} cartTextileItem - Cart Textile Item id.
	 * @param {editQuantityCartTextileItemFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editQuantityCartTextileItem = async <T = any>(cartTextileItem: ReactiveString, formData: editQuantityCartTextileItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/cart_textile_items/editQuantity`,
			[
				cartTextileItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} cartTextileItem - Cart Textile Item id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteCartTextileItem = async <T = any>(cartTextileItem: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/cart_textile_items/delete`,
			[
				cartTextileItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * List carts method
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listCarts = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/carts/list`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View cart method
	 * 
	 * @param {ReactiveString} id - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewCart = async <T = any>(id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/carts/view`,
			[
				id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add cart method
	 * 
	 * Cart's statuses:
	 * - 0 -> Not active - No activity in more than 90 days
	 * - 1 -> Active - Activity in the last 10 days
	 * - 2 -> Idle - No activity in from 11 to 89 days
	 * - 3 -> In checkout - Freezed during checkout
	 * - 4 -> Processed - Checked out
	 * 
	 * @param {addCartFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addCart = async <T = any>(formData: addCartFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/carts/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} cart - Cart id.
	 * @param {editCartFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editCart = async <T = any>(cart: ReactiveString, formData?: editCartFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/carts/edit`,
			[
				cart
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * setProcessed method - Set the status to Processed
	 * This cart should not be modified further
	 * 
	 * @param {ReactiveString} cart - Cart id.
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const setProcessedCart = async <T = any>(cart: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/carts/setProcessed`,
			[
				cart
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} cart - Cart id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteCart = async <T = any>(cart: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/carts/delete`,
			[
				cart
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewForOrganization method
	 * 
	 * @param {ReactiveString} client_organization_id - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForOrganizationCart = async <T = any>(client_organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/carts/viewForOrganization`,
			[
				client_organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * getUserActiveCart method
	 * 
	 * @param {ReactiveString} platform_id - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getUserActiveCartCart = async <T = any>(platform_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/carts/getUserActiveCart`,
			[
				platform_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * isTextileItemAlreadyInCart method
	 * 
	 * @param {ReactiveString} textile_item_id - 
	 * @param {ReactiveString} sample
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 * @deprecated   Use isTextileItemInCart instead 
	 */
	const isTextileItemAlreadyInCartCart = async <T = any>(textile_item_id: ReactiveString, sample: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/carts/isTextileItemAlreadyInCart`,
			[
				textile_item_id, 
				sample
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * isTextileItemInCart method
	 * 
	 * @param {isTextileItemInCartCartFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const isTextileItemInCartCart = async <T = any>(formData: isTextileItemInCartCartFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/carts/isTextileItemInCart`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * isServiceItemAlreadyInCart method
	 * 
	 * @param {ReactiveString} service_id - CartServiceItem service_id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const isServiceItemAlreadyInCartCart = async <T = any>(service_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/carts/isServiceItemAlreadyInCart`,
			[
				service_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * countItems method
	 * 
	 * @param {ReactiveString} cartId - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const countItemsCart = async <T = any>(cartId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/carts/countItems`,
			[
				cartId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * getPrices method
	 * 
	 * @param {ReactiveString} cartId - 
	 * @param {getPricesCartFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getPricesCart = async <T = any>(cartId: ReactiveString, formData: getPricesCartFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/carts/getPrices`,
			[
				cartId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * List method
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listEcosystemTransactionFee = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/ecosystem_transaction_fees/list`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} ecosystemTransactionFee - Ecosystem Transaction Fee id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewEcosystemTransactionFee = async <T = any>(ecosystemTransactionFee: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/ecosystem_transaction_fees/view`,
			[
				ecosystemTransactionFee
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addEcosystemTransactionFeeFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addEcosystemTransactionFee = async <T = any>(formData: addEcosystemTransactionFeeFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/ecosystem_transaction_fees/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} ecosystemTransactionFee - Ecosystem Transaction Fee id.
	 * @param {editEcosystemTransactionFeeFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editEcosystemTransactionFee = async <T = any>(ecosystemTransactionFee: ReactiveString, formData: editEcosystemTransactionFeeFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/ecosystem_transaction_fees/edit`,
			[
				ecosystemTransactionFee
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} ecosystemTransactionFee - Ecosystem Transaction Fee id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteEcosystemTransactionFee = async <T = any>(ecosystemTransactionFee: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/ecosystem_transaction_fees/delete`,
			[
				ecosystemTransactionFee
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} locationPricingRule - Location Pricing Rule id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewLocationPricingRule = async <T = any>(locationPricingRule: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/location_pricing_rules/view`,
			[
				locationPricingRule
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewForContinent method
	 * 
	 * @param {ReactiveString} continent - Location PricingRules continent.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForContinentLocationPricingRule = async <T = any>(continent: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/location_pricing_rules/viewForContinent`,
			[
				continent
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewForOrganization method
	 * 
	 * @param {ReactiveString} organization_id - LocationPricingRules.PricingStrategies organization_id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForOrganizationLocationPricingRule = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/location_pricing_rules/viewForOrganization`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addLocationPricingRuleFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addLocationPricingRule = async <T = any>(formData: addLocationPricingRuleFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/location_pricing_rules/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} locationPricingRule - Location Pricing Rule id.
	 * @param {editLocationPricingRuleFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editLocationPricingRule = async <T = any>(locationPricingRule: ReactiveString, formData: editLocationPricingRuleFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/location_pricing_rules/edit`,
			[
				locationPricingRule
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} locationPricingRule - Location Pricing Rule id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteLocationPricingRule = async <T = any>(locationPricingRule: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/location_pricing_rules/delete`,
			[
				locationPricingRule
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listLoyaltyProgramMembersFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listLoyaltyProgramMembers = async <T = any>(formData: listLoyaltyProgramMembersFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/loyalty_program_members/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} loyaltyProgramMember - Loyalty Program Member id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewLoyaltyProgramMember = async <T = any>(loyaltyProgramMember: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/loyalty_program_members/view`,
			[
				loyaltyProgramMember
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get all Loyalty Program Member matching an Organization ID
	 * 
	 * Supports all the paginate query params:
	 * "?field_list=id,email,first_name&order[field_name]=DIRECTION&limit=10&page=3"
	 * 
	 * @param {ReactiveString} organization_id - Organization
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewAllByOrganizationIdLoyaltyProgramMember = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/loyalty_program_members/viewAllByOrganizationId`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addLoyaltyProgramMemberFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addLoyaltyProgramMember = async <T = any>(formData: addLoyaltyProgramMemberFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/loyalty_program_members/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} loyaltyProgramMember - Loyalty Program Member id.
	 * @param {editLoyaltyProgramMemberFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editLoyaltyProgramMember = async <T = any>(loyaltyProgramMember: ReactiveString, formData?: editLoyaltyProgramMemberFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/loyalty_program_members/edit`,
			[
				loyaltyProgramMember
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} loyaltyProgramMember - Loyalty Program Member id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteLoyaltyProgramMember = async <T = any>(loyaltyProgramMember: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/loyalty_program_members/delete`,
			[
				loyaltyProgramMember
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listLoyaltyProgramTiersFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listLoyaltyProgramTiers = async <T = any>(formData: listLoyaltyProgramTiersFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/loyalty_program_tiers/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} loyaltyProgramTier - Loyalty Program Tier id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewLoyaltyProgramTier = async <T = any>(loyaltyProgramTier: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/loyalty_program_tiers/view`,
			[
				loyaltyProgramTier
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addLoyaltyProgramTierFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addLoyaltyProgramTier = async <T = any>(formData: addLoyaltyProgramTierFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/loyalty_program_tiers/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} loyaltyProgramTier - Loyalty Program Tier id.
	 * @param {editLoyaltyProgramTierFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editLoyaltyProgramTier = async <T = any>(loyaltyProgramTier: ReactiveString, formData?: editLoyaltyProgramTierFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/loyalty_program_tiers/edit`,
			[
				loyaltyProgramTier
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} loyaltyProgramTier - Loyalty Program Tier id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteLoyaltyProgramTier = async <T = any>(loyaltyProgramTier: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/loyalty_program_tiers/delete`,
			[
				loyaltyProgramTier
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listLoyaltyPrograms = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/loyalty_programs/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} loyaltyProgram - Loyalty Program id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewLoyaltyProgram = async <T = any>(loyaltyProgram: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/loyalty_programs/view`,
			[
				loyaltyProgram
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addLoyaltyProgramFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addLoyaltyProgram = async <T = any>(formData: addLoyaltyProgramFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/loyalty_programs/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} loyaltyProgram - Loyalty Program id.
	 * @param {editLoyaltyProgramFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editLoyaltyProgram = async <T = any>(loyaltyProgram: ReactiveString, formData: editLoyaltyProgramFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/loyalty_programs/edit`,
			[
				loyaltyProgram
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} loyaltyProgram - Loyalty Program id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteLoyaltyProgram = async <T = any>(loyaltyProgram: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/loyalty_programs/delete`,
			[
				loyaltyProgram
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get Loyalty Program and its associations for aggregator's organization id.
	 * Get Loyalty Program Tier member data for organization_id or user_id if defined in the request or for all the members if not defined
	 * Return customized data structure for frontend ({organizationLoyaltyProgramTier: "selected loyalty program tier for the organization or null", loyaltyProgramTiers: "aggregator loyalty program tiers"})
	 * 
	 * @param {ReactiveString} aggregator_organization_id - Aggregator organization
	 * @param {customizedViewForOrganizationIdLoyaltyProgramFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const customizedViewForOrganizationIdLoyaltyProgram = async <T = any>(aggregator_organization_id: ReactiveString, formData?: customizedViewForOrganizationIdLoyaltyProgramFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/loyalty_programs/customizedViewForOrganizationId`,
			[
				aggregator_organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listOriginWarehousesFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listOriginWarehouses = async <T = any>(formData?: listOriginWarehousesFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/origin_warehouses/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} originWarehouse - Origin Warehouse id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewOriginWarehouse = async <T = any>(originWarehouse: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/origin_warehouses/view`,
			[
				originWarehouse
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addOriginWarehouseFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addOriginWarehouse = async <T = any>(formData: addOriginWarehouseFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/origin_warehouses/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} originWarehouse - Origin Warehouse id.
	 * @param {editOriginWarehouseFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editOriginWarehouse = async <T = any>(originWarehouse: ReactiveString, formData?: editOriginWarehouseFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/origin_warehouses/edit`,
			[
				originWarehouse
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} originWarehouse - Origin Warehouse id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteOriginWarehouse = async <T = any>(originWarehouse: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/origin_warehouses/delete`,
			[
				originWarehouse
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listPlatformYearlyTotalsFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listPlatformYearlyTotals = async <T = any>(formData: listPlatformYearlyTotalsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/platform_yearly_totals/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} platformYearlyTotal - Platform Yearly Total id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPlatformYearlyTotal = async <T = any>(platformYearlyTotal: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platform_yearly_totals/view`,
			[
				platformYearlyTotal
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * getYearlySpendingByClient method - return yearly spending per year (base on creation date)
	 * 
	 * @param {getYearlySpendingByClientPlatformYearlyTotalFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getYearlySpendingByClientPlatformYearlyTotal = async <T = any>(formData: getYearlySpendingByClientPlatformYearlyTotalFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/platform_yearly_totals/getYearlySpendingByClient`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {incrementYearlyTotalPlatformYearlyTotalFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const incrementYearlyTotalPlatformYearlyTotal = async <T = any>(formData: incrementYearlyTotalPlatformYearlyTotalFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/platform_yearly_totals/incrementYearlyTotal`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get all Platform Yearly Total matching an Organization ID
	 * 
	 * Supports all the paginate query params:
	 * "?field_list=id,email,first_name&order[field_name]=DIRECTION&limit=10&page=3"
	 * 
	 * @param {ReactiveString} organization_id - Organization
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewAllByOrganizationIdPlatformYearlyTotal = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platform_yearly_totals/viewAllByOrganizationId`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addPlatformYearlyTotalFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addPlatformYearlyTotal = async <T = any>(formData: addPlatformYearlyTotalFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/platform_yearly_totals/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} platformYearlyTotal - Platform Yearly Total id.
	 * @param {editPlatformYearlyTotalFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editPlatformYearlyTotal = async <T = any>(platformYearlyTotal: ReactiveString, formData?: editPlatformYearlyTotalFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platform_yearly_totals/edit`,
			[
				platformYearlyTotal
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} platformYearlyTotal - Platform Yearly Total id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deletePlatformYearlyTotal = async <T = any>(platformYearlyTotal: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platform_yearly_totals/delete`,
			[
				platformYearlyTotal
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} platformsTransactionFee - Platforms Transaction Fee id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPlatformsTransactionFee = async <T = any>(platformsTransactionFee: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platforms_transaction_fees/view`,
			[
				platformsTransactionFee
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewFeesForPlatform method
	 * 
	 * @param {ReactiveString} platformId - Platforms Transaction Fee platform_id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewFeesForPlatformPlatformsTransactionFee = async <T = any>(platformId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platforms_transaction_fees/viewFeesForPlatform`,
			[
				platformId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addPlatformsTransactionFeeFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addPlatformsTransactionFee = async <T = any>(formData: addPlatformsTransactionFeeFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/platforms_transaction_fees/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} platformsTransactionFee - Platforms Transaction Fee id.
	 * @param {editPlatformsTransactionFeeFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editPlatformsTransactionFee = async <T = any>(platformsTransactionFee: ReactiveString, formData?: editPlatformsTransactionFeeFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platforms_transaction_fees/edit`,
			[
				platformsTransactionFee
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} platformsTransactionFee - Platforms Transaction Fee id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deletePlatformsTransactionFee = async <T = any>(platformsTransactionFee: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platforms_transaction_fees/delete`,
			[
				platformsTransactionFee
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} price - Price id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPrice = async <T = any>(price: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/prices/view`,
			[
				price
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addPriceFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addPrice = async <T = any>(formData: addPriceFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/prices/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} price - Price id.
	 * @param {editPriceFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editPrice = async <T = any>(price: ReactiveString, formData: editPriceFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/prices/edit`,
			[
				price
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} price - Price id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deletePrice = async <T = any>(price: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/prices/delete`,
			[
				price
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewForPricingModelTier method
	 * 
	 * @param {ReactiveString} pricing_model_tier_id - PricingModelTiers id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForPricingModelTierPrice = async <T = any>(pricing_model_tier_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/prices/viewForPricingModelTier`,
			[
				pricing_model_tier_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewForPricingModel method
	 * 
	 * @param {ReactiveString} pricing_model_id - Prices pricing_model_id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForPricingModelPrice = async <T = any>(pricing_model_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/prices/viewForPricingModel`,
			[
				pricing_model_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * getTextileItemsPrices method
	 * 
	 * @param {getTextileItemsPricesPriceFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getTextileItemsPricesPrice = async <T = any>(formData: getTextileItemsPricesPriceFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/prices/getTextileItemsPrices`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listPricingAgreementsFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listPricingAgreements = async <T = any>(formData?: listPricingAgreementsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/pricing_agreements/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addPricingAgreementFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addPricingAgreement = async <T = any>(formData: addPricingAgreementFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/pricing_agreements/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} pricingAgreement - Pricing Agreement id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPricingAgreement = async <T = any>(pricingAgreement: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pricing_agreements/view`,
			[
				pricingAgreement
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * ViewForOrganization method
	 * 
	 * @param {ReactiveString} organization_id
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForOrganizationPricingAgreement = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pricing_agreements/viewForOrganization`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewForOrganizationByClientsForTextileId method
	 * 
	 * @param {ReactiveString} organization_id
	 * @param {viewForOrganizationByClientsForTextileIdPricingAgreementFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForOrganizationByClientsForTextileIdPricingAgreement = async <T = any>(organization_id: ReactiveString, formData: viewForOrganizationByClientsForTextileIdPricingAgreementFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pricing_agreements/viewForOrganizationByClientsForTextileId`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewForOrganizationByClientsForTextileItemId method
	 * 
	 * @param {ReactiveString} organization_id
	 * @param {viewForOrganizationByClientsForTextileItemIdPricingAgreementFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForOrganizationByClientsForTextileItemIdPricingAgreement = async <T = any>(organization_id: ReactiveString, formData: viewForOrganizationByClientsForTextileItemIdPricingAgreementFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pricing_agreements/viewForOrganizationByClientsForTextileItemId`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get the Pricing Aggreements between 2 organizations for a set of Textile Items
	 * 
	 * @param {ReactiveString} merchant_organization_id
	 * @param {viewForOrganizationbyClientIdPricingAgreementFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForOrganizationbyClientIdPricingAgreement = async <T = any>(merchant_organization_id: ReactiveString, formData: viewForOrganizationbyClientIdPricingAgreementFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pricing_agreements/viewForOrganizationbyClientId`,
			[
				merchant_organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get the Pricing Aggreements between 2 organizations for a set of Textile Items
	 * 
	 * @param {ReactiveString} client_organization_id
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForClientOrganizationIdPricingAgreement = async <T = any>(client_organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pricing_agreements/viewForClientOrganizationId`,
			[
				client_organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} pricingAgreement - Pricing Agreement id.
	 * @param {ReactiveString} organization_id
	 * @param {editPricingAgreementFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editPricingAgreement = async <T = any>(pricingAgreement: ReactiveString, organization_id: ReactiveString, formData: editPricingAgreementFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pricing_agreements/edit`,
			[
				pricingAgreement, 
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * editAgreementsForTextile method
	 * 
	 * @param {editAgreementsForTextilePricingAgreementFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editAgreementsForTextilePricingAgreement = async <T = any>(formData: editAgreementsForTextilePricingAgreementFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/pricing_agreements/editAgreementsForTextile`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} pricingAgreement - Pricing Agreement id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deletePricingAgreement = async <T = any>(pricingAgreement: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pricing_agreements/delete`,
			[
				pricingAgreement
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} pricingModelTier - Pricing Model Tier id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPricingModelTier = async <T = any>(pricingModelTier: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pricing_model_tiers/view`,
			[
				pricingModelTier
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addPricingModelTierFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addPricingModelTier = async <T = any>(formData: addPricingModelTierFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/pricing_model_tiers/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} pricingModelTier - Pricing Model Tier id.
	 * @param {editPricingModelTierFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editPricingModelTier = async <T = any>(pricingModelTier: ReactiveString, formData: editPricingModelTierFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pricing_model_tiers/edit`,
			[
				pricingModelTier
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} pricingModelTier - Pricing Model Tier id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deletePricingModelTier = async <T = any>(pricingModelTier: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pricing_model_tiers/delete`,
			[
				pricingModelTier
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewForPricingTierGroup method
	 * 
	 * @param {ReactiveString} pricing_tier_group_id - PricingModelTiers pricing_tier_group_id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForPricingTierGroupPricingModelTier = async <T = any>(pricing_tier_group_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pricing_model_tiers/viewForPricingTierGroup`,
			[
				pricing_tier_group_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} pricingModel - Pricing Model id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPricingModel = async <T = any>(pricingModel: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pricing_models/view`,
			[
				pricingModel
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewForRevenueModel method
	 * 
	 * @param {ReactiveString} revenue_model_id - PricingModels revenue_model_id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForRevenueModelPricingModel = async <T = any>(revenue_model_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pricing_models/viewForRevenueModel`,
			[
				revenue_model_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addPricingModelFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addPricingModel = async <T = any>(formData: addPricingModelFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/pricing_models/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} pricingModel - Pricing Model id.
	 * @param {editPricingModelFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editPricingModel = async <T = any>(pricingModel: ReactiveString, formData: editPricingModelFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pricing_models/edit`,
			[
				pricingModel
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} pricingModel - Pricing Model id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deletePricingModel = async <T = any>(pricingModel: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pricing_models/delete`,
			[
				pricingModel
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} pricingStrategy - Pricing Strategy id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPricingStrategy = async <T = any>(pricingStrategy: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pricing_strategies/view`,
			[
				pricingStrategy
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewForOrganization method
	 * 
	 * @param {ReactiveString} organizationId - Pricing Strategy organization_id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForOrganizationPricingStrategy = async <T = any>(organizationId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pricing_strategies/viewForOrganization`,
			[
				organizationId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addPricingStrategyFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addPricingStrategy = async <T = any>(formData: addPricingStrategyFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/pricing_strategies/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} pricingStrategy - Pricing Strategy id.
	 * @param {editPricingStrategyFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editPricingStrategy = async <T = any>(pricingStrategy: ReactiveString, formData: editPricingStrategyFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pricing_strategies/edit`,
			[
				pricingStrategy
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} pricingStrategy - Pricing Strategy id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deletePricingStrategy = async <T = any>(pricingStrategy: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pricing_strategies/delete`,
			[
				pricingStrategy
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * addEditLoyaltyProgramTier method
	 * 
	 * @param {addEditLoyaltyProgramTierPricingStrategyFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addEditLoyaltyProgramTierPricingStrategy = async <T = any>(formData: addEditLoyaltyProgramTierPricingStrategyFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/pricing_strategies/addEditLoyaltyProgramTier`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} pricingTierGroup - Pricing Tier Group id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPricingTierGroup = async <T = any>(pricingTierGroup: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pricing_tier_groups/view`,
			[
				pricingTierGroup
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addPricingTierGroupFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addPricingTierGroup = async <T = any>(formData: addPricingTierGroupFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/pricing_tier_groups/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} pricingTierGroup - Pricing Tier Group id.
	 * @param {editPricingTierGroupFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editPricingTierGroup = async <T = any>(pricingTierGroup: ReactiveString, formData: editPricingTierGroupFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pricing_tier_groups/edit`,
			[
				pricingTierGroup
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} pricingTierGroup - Pricing Tier Group id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deletePricingTierGroup = async <T = any>(pricingTierGroup: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pricing_tier_groups/delete`,
			[
				pricingTierGroup
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewForPricingModel method
	 * 
	 * @param {ReactiveString} pricing_model_id - PricingTierGroups pricing_model_id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForPricingModelPricingTierGroup = async <T = any>(pricing_model_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pricing_tier_groups/viewForPricingModel`,
			[
				pricing_model_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewForRevenueModel method
	 * 
	 * @param {ReactiveString} revenue_model_id - PricingModels revenue_model_id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForRevenueModelPricingTierGroup = async <T = any>(revenue_model_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/pricing_tier_groups/viewForRevenueModel`,
			[
				revenue_model_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} revenueModel - Revenue Model id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewRevenueModel = async <T = any>(revenueModel: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/revenue_models/view`,
			[
				revenueModel
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addRevenueModelFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addRevenueModel = async <T = any>(formData: addRevenueModelFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/revenue_models/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} revenueModel - Revenue Model id.
	 * @param {editRevenueModelFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editRevenueModel = async <T = any>(revenueModel: ReactiveString, formData: editRevenueModelFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/revenue_models/edit`,
			[
				revenueModel
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} revenueModel - Revenue Model id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteRevenueModel = async <T = any>(revenueModel: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/revenue_models/delete`,
			[
				revenueModel
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listSalesTaxesFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listSalesTaxes = async <T = any>(formData?: listSalesTaxesFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/sales_taxes/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} salesTax - Sales Tax id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewSalesTax = async <T = any>(salesTax: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/sales_taxes/view`,
			[
				salesTax
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewForCountry method
	 * 
	 * @param {ReactiveString} country - Sales Tax country.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForCountrySalesTax = async <T = any>(country: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/sales_taxes/viewForCountry`,
			[
				country
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewForOrganization method
	 * 
	 * @param {ReactiveString} organization_id - SalesTaxes.PricingStrategies organization_id.
	 * @param {viewForOrganizationSalesTaxFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForOrganizationSalesTax = async <T = any>(organization_id: ReactiveString, formData?: viewForOrganizationSalesTaxFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/sales_taxes/viewForOrganization`,
			[
				organization_id
			]
		);
		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * getApplicableTax method
	 * 
	 * @param {getApplicableTaxSalesTaxFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getApplicableTaxSalesTax = async <T = any>(formData?: getApplicableTaxSalesTaxFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/sales_taxes/getApplicableTax`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addSalesTaxFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addSalesTax = async <T = any>(formData: addSalesTaxFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/sales_taxes/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} salesTax - Sales Tax id.
	 * @param {editSalesTaxFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editSalesTax = async <T = any>(salesTax: ReactiveString, formData?: editSalesTaxFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/sales_taxes/edit`,
			[
				salesTax
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} salesTax - Sales Tax id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteSalesTax = async <T = any>(salesTax: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/sales_taxes/delete`,
			[
				salesTax
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listServiceFeesFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listServiceFees = async <T = any>(formData?: listServiceFeesFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/service_fees/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} serviceFee - Service Fee id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewServiceFee = async <T = any>(serviceFee: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/service_fees/view`,
			[
				serviceFee
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addServiceFeeFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addServiceFee = async <T = any>(formData: addServiceFeeFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/service_fees/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} serviceFee - Service Fee id.
	 * @param {editServiceFeeFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editServiceFee = async <T = any>(serviceFee: ReactiveString, formData?: editServiceFeeFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/service_fees/edit`,
			[
				serviceFee
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} serviceFee - Service Fee id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteServiceFee = async <T = any>(serviceFee: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/service_fees/delete`,
			[
				serviceFee
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * addOrganizationDefaultsForSupplyChain method
	 * 
	 * @param {ReactiveString} organization_id
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addOrganizationDefaultsForSupplyChainSubscription = async <T = any>(organization_id: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/subscriptions/addOrganizationDefaultsForSupplyChain`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add Location pricing rules default to organization method
	 * 
	 * @param {ReactiveString} organization_id - 
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addOrganizationDefaultsLocationPricingRulesSubscription = async <T = any>(organization_id: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/subscriptions/addOrganizationDefaultsLocationPricingRules`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add Volume pricing rules default to organization method
	 * 
	 * @param {ReactiveString} organization_id - 
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addOrganizationDefaultsVolumePricingRulesSubscription = async <T = any>(organization_id: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/subscriptions/addOrganizationDefaultsVolumePricingRules`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * addOrganizationDefaultsForMarketplace method
	 * 
	 * @param {addOrganizationDefaultsForMarketplaceSubscriptionFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addOrganizationDefaultsForMarketplaceSubscription = async <T = any>(formData: addOrganizationDefaultsForMarketplaceSubscriptionFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/subscriptions/addOrganizationDefaultsForMarketplace`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * addOrganizationDefaultsForMarketplace method
	 * 
	 * @param {addOrganizationDefaultsForPartnerSubscriptionFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addOrganizationDefaultsForPartnerSubscription = async <T = any>(formData: addOrganizationDefaultsForPartnerSubscriptionFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/subscriptions/addOrganizationDefaultsForPartner`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} textileItemsPricing - Textile Items Pricing id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewTextileItemsPricing = async <T = any>(textileItemsPricing: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textile_items_pricings/view`,
			[
				textileItemsPricing
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewForTextile method
	 * 
	 * @param {ReactiveString} textile_item_id - TextileItemsPricings textile_item_id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForTextileTextileItemsPricing = async <T = any>(textile_item_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textile_items_pricings/viewForTextile`,
			[
				textile_item_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewForOrganization method
	 * 
	 * @param {ReactiveString} organization_id - TextileItemsPricings.TextilesPricings.PricingStrategies organization_id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForOrganizationTextileItemsPricing = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textile_items_pricings/viewForOrganization`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addTextileItemsPricingFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addTextileItemsPricing = async <T = any>(formData: addTextileItemsPricingFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/textile_items_pricings/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} textileItemsPricing - Textile Items Pricing id.
	 * @param {editTextileItemsPricingFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editTextileItemsPricing = async <T = any>(textileItemsPricing: ReactiveString, formData?: editTextileItemsPricingFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textile_items_pricings/edit`,
			[
				textileItemsPricing
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} textileItemsPricing - Textile Items Pricing id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteTextileItemsPricing = async <T = any>(textileItemsPricing: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textile_items_pricings/delete`,
			[
				textileItemsPricing
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} textilesPricing - Textiles Pricing id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewTextilesPricing = async <T = any>(textilesPricing: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textiles_pricings/view`,
			[
				textilesPricing
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewForOrganization method
	 * 
	 * @param {ReactiveString} organizationId - PricingStrategies organization_id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForOrganizationTextilesPricing = async <T = any>(organizationId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textiles_pricings/viewForOrganization`,
			[
				organizationId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addTextilesPricingFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addTextilesPricing = async <T = any>(formData: addTextilesPricingFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/textiles_pricings/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} textilesPricing - Textiles Pricing id.
	 * @param {editTextilesPricingFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editTextilesPricing = async <T = any>(textilesPricing: ReactiveString, formData?: editTextilesPricingFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textiles_pricings/edit`,
			[
				textilesPricing
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} textilesPricing - Textiles Pricing id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteTextilesPricing = async <T = any>(textilesPricing: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textiles_pricings/delete`,
			[
				textilesPricing
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listVolumePricingMembersFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listVolumePricingMembers = async <T = any>(formData?: listVolumePricingMembersFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/volume_pricing_members/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} volumePricingMember - Volume Pricing Member id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewVolumePricingMember = async <T = any>(volumePricingMember: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/volume_pricing_members/view`,
			[
				volumePricingMember
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get all Volume Pricing Member matching an Organization ID
	 * 
	 * Supports all the paginate query params:
	 * "?field_list=id,email,first_name&order[field_name]=DIRECTION&limit=10&page=3"
	 * 
	 * @param {ReactiveString} organization_id - Organization
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewAllByOrganizationIdVolumePricingMember = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/volume_pricing_members/viewAllByOrganizationId`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addVolumePricingMemberFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addVolumePricingMember = async <T = any>(formData: addVolumePricingMemberFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/volume_pricing_members/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} volumePricingMember - Volume Pricing Member id.
	 * @param {editVolumePricingMemberFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editVolumePricingMember = async <T = any>(volumePricingMember: ReactiveString, formData: editVolumePricingMemberFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/volume_pricing_members/edit`,
			[
				volumePricingMember
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} volumePricingMember - Volume Pricing Member id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteVolumePricingMember = async <T = any>(volumePricingMember: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/volume_pricing_members/delete`,
			[
				volumePricingMember
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} volumePricingRule - Volume Pricing Rule id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewVolumePricingRule = async <T = any>(volumePricingRule: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/volume_pricing_rules/view`,
			[
				volumePricingRule
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewForOrganization method
	 * 
	 * @param {ReactiveString} organization_id - PricingStrategies organization_id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForOrganizationVolumePricingRule = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/volume_pricing_rules/viewForOrganization`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewForVolume method
	 * 
	 * @param {ReactiveString} tier_name - VolumePricingRules tier_name.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForVolumeVolumePricingRule = async <T = any>(tier_name: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/volume_pricing_rules/viewForVolume`,
			[
				tier_name
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addVolumePricingRuleFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addVolumePricingRule = async <T = any>(formData: addVolumePricingRuleFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/volume_pricing_rules/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} volumePricingRule - Volume Pricing Rule id.
	 * @param {editVolumePricingRuleFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editVolumePricingRule = async <T = any>(volumePricingRule: ReactiveString, formData: editVolumePricingRuleFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/volume_pricing_rules/edit`,
			[
				volumePricingRule
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} volumePricingRule - Volume Pricing Rule id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteVolumePricingRule = async <T = any>(volumePricingRule: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/volume_pricing_rules/delete`,
			[
				volumePricingRule
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get Volume pricing rules and its associations for merchant's organization id.
	 * Get Volume pricing tier member data for organization_id or user_id if defined in the request or for all the members if not defined
	 * Return customized data structure for frontend ({organizationPricingTier: "selected pricing tier for the organization or user", volumePricingRules: "merchant volume pricing rules tiers"})
	 * 
	 * @param {ReactiveString} merchant_organization_id - Merchant organization
	 * @param {customizedViewForOrganizationIdVolumePricingRuleFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const customizedViewForOrganizationIdVolumePricingRule = async <T = any>(merchant_organization_id: ReactiveString, formData?: customizedViewForOrganizationIdVolumePricingRuleFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/volume_pricing_rules/customizedViewForOrganizationId`,
			[
				merchant_organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};


	return {
		call,
		viewBillingCycle,
		viewForRevenueModelBillingCycle,
		addBillingCycle,
		editBillingCycle,
		deleteBillingCycle,
		viewCartServiceItem,
		addCartServiceItem,
		editCartServiceItem,
		deleteCartServiceItem,
		viewForCartCartServiceItem,
		viewCartTextileItem,
		viewForCartCartTextileItem,
		addCartTextileItem,
		editCartTextileItem,
		editQuantityCartTextileItem,
		deleteCartTextileItem,
		listCarts,
		viewCart,
		addCart,
		editCart,
		setProcessedCart,
		deleteCart,
		viewForOrganizationCart,
		getUserActiveCartCart,
		isTextileItemAlreadyInCartCart,
		isTextileItemInCartCart,
		isServiceItemAlreadyInCartCart,
		countItemsCart,
		getPricesCart,
		listEcosystemTransactionFee,
		viewEcosystemTransactionFee,
		addEcosystemTransactionFee,
		editEcosystemTransactionFee,
		deleteEcosystemTransactionFee,
		viewLocationPricingRule,
		viewForContinentLocationPricingRule,
		viewForOrganizationLocationPricingRule,
		addLocationPricingRule,
		editLocationPricingRule,
		deleteLocationPricingRule,
		listLoyaltyProgramMembers,
		viewLoyaltyProgramMember,
		viewAllByOrganizationIdLoyaltyProgramMember,
		addLoyaltyProgramMember,
		editLoyaltyProgramMember,
		deleteLoyaltyProgramMember,
		listLoyaltyProgramTiers,
		viewLoyaltyProgramTier,
		addLoyaltyProgramTier,
		editLoyaltyProgramTier,
		deleteLoyaltyProgramTier,
		listLoyaltyPrograms,
		viewLoyaltyProgram,
		addLoyaltyProgram,
		editLoyaltyProgram,
		deleteLoyaltyProgram,
		customizedViewForOrganizationIdLoyaltyProgram,
		listOriginWarehouses,
		viewOriginWarehouse,
		addOriginWarehouse,
		editOriginWarehouse,
		deleteOriginWarehouse,
		listPlatformYearlyTotals,
		viewPlatformYearlyTotal,
		getYearlySpendingByClientPlatformYearlyTotal,
		incrementYearlyTotalPlatformYearlyTotal,
		viewAllByOrganizationIdPlatformYearlyTotal,
		addPlatformYearlyTotal,
		editPlatformYearlyTotal,
		deletePlatformYearlyTotal,
		viewPlatformsTransactionFee,
		viewFeesForPlatformPlatformsTransactionFee,
		addPlatformsTransactionFee,
		editPlatformsTransactionFee,
		deletePlatformsTransactionFee,
		viewPrice,
		addPrice,
		editPrice,
		deletePrice,
		viewForPricingModelTierPrice,
		viewForPricingModelPrice,
		getTextileItemsPricesPrice,
		listPricingAgreements,
		addPricingAgreement,
		viewPricingAgreement,
		viewForOrganizationPricingAgreement,
		viewForOrganizationByClientsForTextileIdPricingAgreement,
		viewForOrganizationByClientsForTextileItemIdPricingAgreement,
		viewForOrganizationbyClientIdPricingAgreement,
		viewForClientOrganizationIdPricingAgreement,
		editPricingAgreement,
		editAgreementsForTextilePricingAgreement,
		deletePricingAgreement,
		viewPricingModelTier,
		addPricingModelTier,
		editPricingModelTier,
		deletePricingModelTier,
		viewForPricingTierGroupPricingModelTier,
		viewPricingModel,
		viewForRevenueModelPricingModel,
		addPricingModel,
		editPricingModel,
		deletePricingModel,
		viewPricingStrategy,
		viewForOrganizationPricingStrategy,
		addPricingStrategy,
		editPricingStrategy,
		deletePricingStrategy,
		addEditLoyaltyProgramTierPricingStrategy,
		viewPricingTierGroup,
		addPricingTierGroup,
		editPricingTierGroup,
		deletePricingTierGroup,
		viewForPricingModelPricingTierGroup,
		viewForRevenueModelPricingTierGroup,
		viewRevenueModel,
		addRevenueModel,
		editRevenueModel,
		deleteRevenueModel,
		listSalesTaxes,
		viewSalesTax,
		viewForCountrySalesTax,
		viewForOrganizationSalesTax,
		getApplicableTaxSalesTax,
		addSalesTax,
		editSalesTax,
		deleteSalesTax,
		listServiceFees,
		viewServiceFee,
		addServiceFee,
		editServiceFee,
		deleteServiceFee,
		addOrganizationDefaultsForSupplyChainSubscription,
		addOrganizationDefaultsLocationPricingRulesSubscription,
		addOrganizationDefaultsVolumePricingRulesSubscription,
		addOrganizationDefaultsForMarketplaceSubscription,
		addOrganizationDefaultsForPartnerSubscription,
		viewTextileItemsPricing,
		viewForTextileTextileItemsPricing,
		viewForOrganizationTextileItemsPricing,
		addTextileItemsPricing,
		editTextileItemsPricing,
		deleteTextileItemsPricing,
		viewTextilesPricing,
		viewForOrganizationTextilesPricing,
		addTextilesPricing,
		editTextilesPricing,
		deleteTextilesPricing,
		listVolumePricingMembers,
		viewVolumePricingMember,
		viewAllByOrganizationIdVolumePricingMember,
		addVolumePricingMember,
		editVolumePricingMember,
		deleteVolumePricingMember,
		viewVolumePricingRule,
		viewForOrganizationVolumePricingRule,
		viewForVolumeVolumePricingRule,
		addVolumePricingRule,
		editVolumePricingRule,
		deleteVolumePricingRule,
		customizedViewForOrganizationIdVolumePricingRule,
	}
};